/**
 * Cvent Alumni CSV → Interspire format conversion.
 * Ported from CVENT Alumni to Interspire converter.js.
 */

export const EXPECTED_HEADERS = [
  'Email Address',
  'Company Name',
  'First Name',
  'Last Name',
  'Title',
  'Start Date',
  'Work Phone Number',
  'Participant',
  'Work City',
  'Work State/Province Code',
  'Event Title',
  'Event City',
  'Event State/Province Code',
  'Event Location',
  'Planner First Name',
];

export const OUTPUT_HEADERS = [
  'Email Address',
  'Agency',
  'First Name',
  'Last Name',
  'Title',
  'Entry / Event Date',
  'Phone',
  'City',
  'State',
  'Event Location',
  'Event Type',
  'Staff Member',
  'Area Code',
];

export const PREVIEW_ROW_COUNT = 10;
export const TRUNCATE_CELL_LENGTH = 28;

const EVENT_TYPE_KEYWORDS = ['Subawards', 'Management', 'Writing'];

function cleanPhone(value: string): string {
  if (!value || typeof value !== 'string') return '';
  const digits = value.replace(/\D/g, '');
  const normalized = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return normalized.slice(0, 10);
}

function getAreaCode(cleanPhoneStr: string): string {
  return cleanPhoneStr.length >= 3 ? cleanPhoneStr.slice(0, 3) : '';
}

function formatShortDate(value: string): string {
  if (!value || typeof value !== 'string') return '';
  const s = value.trim();
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const y = iso[1].slice(-2);
    const m = String(parseInt(iso[2], 10));
    const d = String(parseInt(iso[3], 10));
    return m + '/' + d + '/' + y;
  }
  return s;
}

function getEventType(eventTitle: string): string {
  if (!eventTitle || typeof eventTitle !== 'string') return '';
  const t = eventTitle.toLowerCase();
  for (let i = 0; i < EVENT_TYPE_KEYWORDS.length; i++) {
    if (t.indexOf(EVENT_TYPE_KEYWORDS[i].toLowerCase()) !== -1) {
      return EVENT_TYPE_KEYWORDS[i];
    }
  }
  return '';
}

function unquoteCell(s: string): string {
  if (typeof s !== 'string' || s.length < 2) return s;
  if (s[0] === '"' && s[s.length - 1] === '"') {
    return s.slice(1, -1).replace(/""/g, '"');
  }
  return s;
}

function parseCSV(text: string): string[][] {
  const lines: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
    } else if (inQuotes) {
      if (ch === '\\') {
        current += text[++i] ?? '';
      } else {
        current += ch;
      }
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      lines.push(current);
      current = '';
    } else if (ch === ',' && !inQuotes) {
      current += '\0';
    } else {
      current += ch;
    }
  }
  if (current) lines.push(current);

  return lines.map((line) => {
    const cells: string[] = [];
    let cell = '';
    let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        q = !q;
      } else if (c === '\0') {
        cells.push(unquoteCell(q ? cell : cell.trim()));
        cell = '';
      } else {
        cell += c;
      }
    }
    cells.push(unquoteCell(q ? cell : cell.trim()));
    return cells;
  });
}

function stripBOM(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

function findHeaderIndex(headers: string[], expected: string): number {
  const normalized = headers.map((h) => (h || '').trim());
  const idx = normalized.findIndex((h) => h.toLowerCase() === expected.toLowerCase());
  return idx >= 0 ? idx : normalized.indexOf(expected);
}

interface ValidateResult {
  indices: Record<string, number>;
  missing: string[];
}

function validateHeaders(headers: string[]): ValidateResult {
  const trimmed = headers.map((h) => (h || '').trim());
  const missing: string[] = [];
  const indices: Record<string, number> = {};

  for (let i = 0; i < EXPECTED_HEADERS.length; i++) {
    const exp = EXPECTED_HEADERS[i];
    const idx = findHeaderIndex(trimmed, exp);
    indices[exp] = idx;
    if (idx < 0) missing.push(exp);
  }

  return { indices, missing };
}

function convertRow(row: string[], indices: Record<string, number>): string[] {
  const get = (key: string): string => {
    const idx = indices[key];
    return idx >= 0 && row[idx] !== undefined ? String(row[idx] || '').trim() : '';
  };

  const rawPhone = get('Work Phone Number');
  const phone = cleanPhone(rawPhone);
  const areaCode = getAreaCode(phone);

  return [
    get('Email Address'),
    get('Company Name'),
    get('First Name'),
    get('Last Name'),
    get('Title'),
    formatShortDate(get('Start Date')),
    phone,
    get('Event City'),
    get('Event State/Province Code'),
    get('Event Location'),
    getEventType(get('Event Title')),
    get('Planner First Name'),
    areaCode,
  ];
}

function escapeCSVCell(value: unknown): string {
  const s = String(value ?? '');
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

export function toCSV(rows: string[][]): string {
  const headerLine = OUTPUT_HEADERS.map(escapeCSVCell).join(',');
  const dataLines = rows.map((row) => row.map(escapeCSVCell).join(','));
  return [headerLine, ...dataLines].join('\r\n');
}

export interface ParseAndConvertResult {
  rows: string[][];
  missingHeaders: string[];
}

/**
 * Parse CSV text (Cvent format) and convert to Interspire-style rows.
 * Returns converted rows and list of missing expected headers (for warnings).
 */
export function parseAndConvertCsv(text: string): ParseAndConvertResult {
  const stripped = stripBOM(text);
  const parsed = parseCSV(stripped);
  if (parsed.length === 0) {
    return { rows: [], missingHeaders: EXPECTED_HEADERS.slice() };
  }

  const headers = parsed[0];
  const validation = validateHeaders(headers);

  const dataRows = parsed.slice(1).filter((row) =>
    row.some((cell) => String(cell || '').trim() !== '')
  );

  const rows = dataRows.map((row) => convertRow(row, validation.indices));
  return { rows, missingHeaders: validation.missing };
}
